import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, X } from 'lucide-react';
import styles from './SearchOverlay.module.scss';

export interface SearchOverlayHandle {
  skipNextHistoryBack: () => void;
}

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  placeholder?: string;
  shortcutHint?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** When false, skips history push/pop (avoids conflict with parent history, e.g. when auto-opened in a wizard) */
  useHistory?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Omit footer top border when it should align flush with scrolling content (e.g. category picker). */
  footerFlush?: boolean;
}

const SearchOverlay = forwardRef<SearchOverlayHandle, SearchOverlayProps>(
  function SearchOverlay(
    {
      isOpen,
      onClose,
      query,
      onQueryChange,
      placeholder = 'Search...',
      shortcutHint,
      onKeyDown,
      useHistory = true,
      children,
      footer,
      footerFlush = false,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const closedByPopStateRef = useRef(false);

    useImperativeHandle(ref, () => ({
      skipNextHistoryBack: () => {
        closedByPopStateRef.current = true;
      },
    }));

    // Intercept manual UI close triggers to bypass window.history.back() during unmount
    // This is to fix when closing the overlay, window.history.back() is called twice.
    const handleManualClose = useCallback(() => {
      closedByPopStateRef.current = true;
      onClose();
    }, [onClose]);

    useEffect(() => {
      if (!isOpen) return;
      requestAnimationFrame(() => {
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        if (input.value.length > 0) {
          const end = input.value.length;
          input.setSelectionRange(end, end);
        }
      });
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen || !useHistory) return;

      closedByPopStateRef.current = false;
      window.history.pushState({ searchOverlay: true }, '');

      const handlePopState = () => {
        closedByPopStateRef.current = true;
        onClose();
      };

      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
        if (!closedByPopStateRef.current) {
          window.history.back();
        }
      };
    }, [isOpen, onClose, useHistory]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleManualClose();
        return;
      }
      onKeyDown?.(e);
    };

    if (!isOpen) return null;

    return createPortal(
      <div className={styles.overlay} onMouseDown={handleManualClose}>
        <div
          className={styles.panel}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-label={t('components.SearchOverlay.SearchOverlay.search_1')}
        >
          <div className={styles.inputWrapper}>
            <button
              className={styles.backButton}
              onClick={handleManualClose}
              aria-label={t('components.SearchOverlay.SearchOverlay.close_2')}
              type="button"
            >
              <ArrowLeft size={20} />
            </button>
            <input
              ref={inputRef}
              type="text"
              className={styles.input}
              placeholder={placeholder}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              aria-label={t('components.SearchOverlay.SearchOverlay.search_1')}
              autoComplete="off"
            />
            {query && (
              <button
                className={styles.clearButton}
                onClick={() => {
                  onQueryChange('');
                  inputRef.current?.focus();
                }}
                aria-label={t(
                  'components.SearchOverlay.SearchOverlay.clear_search_4',
                )}
                type="button"
              >
                <X size={16} />
              </button>
            )}
            {shortcutHint && (
              <kbd className={styles.shortcutHint}>{shortcutHint}</kbd>
            )}
          </div>
          <div className={styles.results}>{children}</div>
          {footer && (
            <div
              className={`${styles.footer}${
                footerFlush ? ` ${styles.footerFlush}` : ''
              }`}
            >
              {footer}
            </div>
          )}
        </div>
      </div>,
      document.body,
    );
  },
);

export default SearchOverlay;
